import React, { createContext, useCallback, useContext, useState, ReactNode } from "react";

interface BreadcrumbOverrides {
  [path: string]: string;
}

interface BreadcrumbsContextType {
  overrides: BreadcrumbOverrides;
  setOverride: (path: string, label: string) => void;
  clearOverride: (path: string) => void;
  clearAllOverrides: () => void;
  linkOverrides: BreadcrumbOverrides;
  setLinkOverride: (path: string, linkTo: string) => void;
  clearLinkOverride: (path: string) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextType | undefined>(undefined);

export const BreadcrumbsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [overrides, setOverrides] = useState<BreadcrumbOverrides>({});
  const [linkOverrides, setLinkOverrides] = useState<BreadcrumbOverrides>({});

  const setOverride = useCallback((path: string, label: string) => {
    setOverrides((prev) => ({ ...prev, [path]: label }));
  }, []);

  const clearOverride = useCallback((path: string) => {
    setOverrides((prev) => {
      const newOverrides = { ...prev };
      delete newOverrides[path];
      return newOverrides;
    });
  }, []);

  const clearAllOverrides = useCallback(() => {
    setOverrides({});
  }, []);

  const setLinkOverride = useCallback((path: string, linkTo: string) => {
    setLinkOverrides((prev) => ({ ...prev, [path]: linkTo }));
  }, []);

  const clearLinkOverride = useCallback((path: string) => {
    setLinkOverrides((prev) => {
      const newOverrides = { ...prev };
      delete newOverrides[path];
      return newOverrides;
    });
  }, []);

  return (
    <BreadcrumbsContext.Provider value={{ overrides, setOverride, clearOverride, clearAllOverrides, linkOverrides, setLinkOverride, clearLinkOverride }}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbsContext);
  if (context === undefined) {
    throw new Error("useBreadcrumbs must be used within a BreadcrumbsProvider");
  }
  return context;
};
